import React, { createContext, FC, memo, useContext, useEffect, useReducer, Reducer } from 'react';
import { useProduct } from '@montana/containers/product';
import { Action } from 'redux';

export const PDPContext = createContext<IPDPContext>({} as IPDPContext);

interface IPdpReducerState {
    activeTab?: PdpSpecificationsTab;
    activeVariationPicker?: string;
    scrollToTabUpdate?: number;

}

enum PdpAction {
    SET_ACTIVE_TAB,
    SET_ACTIVE_VARIATION_PICKER,
    SCROLL_TO_TAB
}

export enum PdpSpecificationsTab {
    SPECIFICATION = 'pdp-m14-specifications',
    DESCRIPTION = 'pdp-m14-description',
    RESOURCES = 'pdp-m14-resources'

}

interface ISetActiveTabAction extends Action<PdpAction.SET_ACTIVE_TAB> {
    activeTab: PdpSpecificationsTab | undefined;
}

interface ISetActiveVariationPickerAction extends Action<PdpAction.SET_ACTIVE_VARIATION_PICKER> {
    activeVariationPicker: string | undefined;
}

interface IScrollToTabAction extends Action<PdpAction.SCROLL_TO_TAB> {
    tabId: PdpSpecificationsTab;
}

type IPdpReducerAction = ISetActiveTabAction | ISetActiveVariationPickerAction | IScrollToTabAction;

const pdpReducer: Reducer<IPdpReducerState, IPdpReducerAction> = (s = {}, a) => {
    switch (a.type) {
        case PdpAction.SET_ACTIVE_TAB: {
            return { ...s, activeTab: a.activeTab };
        }
        case PdpAction.SET_ACTIVE_VARIATION_PICKER: {
            return { ...s, activeVariationPicker: a.activeVariationPicker };
        }
        case PdpAction.SCROLL_TO_TAB: {
            return { ...s, activeTab: a.tabId, scrollToTabUpdate: Date.now() };
        }
        default: {
            return s;
        }
    }
}

interface IPDPContext extends IPdpReducerState {
    setActiveVariationPicker: (pickerName?: string) => void;
    setActiveTab: (tabId: PdpSpecificationsTab | undefined) => void;
    scrollToTab: (tabId: PdpSpecificationsTab) => void;
}

export const PDPProvider: FC = memo(({ children }) => {
    const { productTitleH1 } = useProduct();
    const [{ activeTab, activeVariationPicker, scrollToTabUpdate: scrollToDescriptionUpdate }, dispatch] = useReducer(pdpReducer, {});

    const setActiveVariationPicker = (s: string | undefined) => dispatch({ type: PdpAction.SET_ACTIVE_VARIATION_PICKER, activeVariationPicker: s });
    const setActiveTab = (t: PdpSpecificationsTab | undefined) => dispatch({ type: PdpAction.SET_ACTIVE_TAB, activeTab: t });
    const scrollToTab = (t: PdpSpecificationsTab) => dispatch({ type: PdpAction.SCROLL_TO_TAB, tabId: t });

    // Ensure that we close all pickers when switching products.
    useEffect(() => {
        setActiveVariationPicker(undefined);
    }, [productTitleH1]);

    return (
        <PDPContext.Provider value={{ activeVariationPicker, setActiveVariationPicker, activeTab, setActiveTab, scrollToTabUpdate: scrollToDescriptionUpdate, scrollToTab }}>
            {children}
        </PDPContext.Provider>
    );
});

export function usePDPContext() {
    const context = useContext(PDPContext)

    if (context === undefined) {
        throw new Error('usePDPContext must be used within a PDPProvider')
    }

    return context;
}
